import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import {
  racesQueryOptions,
  seasonsQueryOptions,
} from "@/tools/f1-fantasy/hooks/use-f1-data";
import { getNextOrLatestRace } from "@/tools/f1-fantasy/lib/race-utils";
import { FantasyPoints } from "@/tools/f1-fantasy/pages/FantasyPoints";

// Fantasy data is only available for seasons >= 2023
const MIN_FANTASY_SEASON = 2023;

// URL search params — persisted across reloads
const fantasyPointsSearchSchema = z.object({
  season: z.coerce.number().optional(),
  race: z.string().optional(),
});

export const Route = createFileRoute("/f1-fantasy/fantasy-points")({
  validateSearch: fantasyPointsSearchSchema,
  head: () => ({
    meta: [
      { title: "F1 Fantasy Points Breakdown | Toolverse" },
      {
        name: "description",
        content:
          "Detailed F1 Fantasy points breakdown for every driver and constructor, across all seasons. Race-by-race analysis with DQ scoring and performance metrics.",
      },
      {
        property: "og:title",
        content: "F1 Fantasy Points Breakdown | Toolverse",
      },
      {
        property: "og:description",
        content:
          "Race-by-race F1 Fantasy points data for every driver and constructor, across all seasons from 2023 onwards.",
      },
      ...i18nOgMeta("/f1-fantasy/fantasy-points"),
    ],
    links: [...i18nLinks("/f1-fantasy/fantasy-points")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: localeUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Fantasy",
              item: localeUrl("/f1-fantasy"),
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Fantasy Points",
              item: localeUrl("/f1-fantasy/fantasy-points"),
            },
          ],
        }),
      },
    ],
  }),
  component: FantasyPoints,
  // Full SSR: skeleton UI renders server-side for fast initial paint
  // Seasons and races are awaited (fast), fantasy data loads client-side with skeletons
  ssr: true,
  // Expose search params to the loader via loaderDeps (TanStack Router pattern)
  loaderDeps: ({ search }) => ({
    season: search.season,
    race: search.race,
  }),
  loader: async ({ context, deps }) => {
    const { season: searchSeason, race: searchRace } = deps;

    // Step 1: Fetch all seasons to find the latest one with fantasy data
    const allSeasons = await context.queryClient.ensureQueryData(
      seasonsQueryOptions(),
    );

    // Filter to only fantasy seasons (>= 2023) and get the latest
    const fantasySeasons = allSeasons?.filter(
      (s) => s.year >= MIN_FANTASY_SEASON,
    );
    const latestSeason = fantasySeasons?.[0]?.year; // Seasons are ordered by year DESC

    if (!latestSeason) {
      return { season: null, raceSlug: null };
    }

    // Use URL search param if provided and valid, otherwise fall back to latest season
    const season =
      searchSeason && searchSeason >= MIN_FANTASY_SEASON
        ? searchSeason
        : latestSeason;

    // Step 2: Fetch races for the selected season
    const races = await context.queryClient.ensureQueryData(
      racesQueryOptions(season),
    );

    // Use URL race slug if valid for this season, otherwise default to latest completed race
    // (Fantasy data only exists for past races, so we prefer the last completed race)
    const now = new Date();
    const pastRaces = (races ?? []).filter(
      (r) => r.date && new Date(r.date) < now,
    );
    const latestCompletedRace =
      pastRaces.length > 0 ? pastRaces[pastRaces.length - 1] : null;
    const raceSlug =
      (searchRace && races?.find((r) => r.slug === searchRace)?.slug) ||
      latestCompletedRace?.slug ||
      getNextOrLatestRace(races ?? [])?.slug ||
      null;

    // NOTE: Fantasy results are NOT prefetched here due to serialization issues
    // with nested driver/constructor objects. They load client-side with skeletons.

    return { season, raceSlug };
  },
});
